import {
  OCCUPIABLE_KIND,
  PATH_SEPARATOR,
  type ApiStorageNode,
  type SlotState,
  type StorageCapacity,
} from '@/types/storage'

/**
 * Pure storage-hierarchy helpers.
 *
 * Kept out of components deliberately: vitest here runs in a node environment
 * and only matches `src/**\/*.test.ts`, so logic living in a `.tsx` file would
 * never be covered by a test.
 */

/** Only a POSITION holds stock. Mirrors the server rule exactly. */
export function isOccupiable(kind: string): boolean {
  return kind === OCCUPIABLE_KIND
}

export function pathSegments(path: string): string[] {
  return path.split(PATH_SEPARATOR).filter((s) => s.length > 0)
}

/**
 * True when `candidatePath` is at or beneath `ancestorPath`.
 *
 * Segment-aware on purpose: a raw `startsWith` would report "LN2-A/R10" as
 * living under "LN2-A/R1", which silently pulls an unrelated rack into a
 * subtree view.
 */
export function isAtOrUnder(candidatePath: string, ancestorPath: string): boolean {
  return (
    candidatePath === ancestorPath || candidatePath.startsWith(`${ancestorPath}${PATH_SEPARATOR}`)
  )
}

/**
 * Render a location for display. The path is already materialised by the
 * server, so this costs nothing per row — which is the whole reason it is
 * denormalised there.
 */
export function formatLocation(path: string, separator = ' › '): string {
  return pathSegments(path).join(separator)
}

/**
 * Derive capacity for a set of nodes.
 *
 * Mirrors `deriveCapacity` on the server so the two can never disagree about
 * what a number means. Capacity is always computed, never stored: a cached
 * count goes stale the moment a put-away races a block.
 */
export function deriveCapacity(
  nodes: ApiStorageNode[],
  occupiedIds: ReadonlySet<string>,
): StorageCapacity {
  const positions = nodes.filter((n) => isOccupiable(n.kind) && !n.isHoldingArea)
  const declared = positions.length
  const blocked = positions.filter((p) => p.blocked).length
  const spaces = declared - blocked
  // A blocked-and-occupied slot should be impossible (the server refuses to
  // block an occupied position), but must never push `remaining` negative and
  // turn the figure into nonsense if one ever exists.
  const used = positions.filter((p) => occupiedIds.has(p.id) && !p.blocked).length
  const remaining = spaces - used
  return { declared, blocked, spaces, used, remaining, full: declared > 0 && remaining <= 0 }
}

export function slotState(node: ApiStorageNode, occupiedIds: ReadonlySet<string>): SlotState {
  // Blocked wins over occupied: an operator scanning the map needs to see the
  // slot they cannot use, and the server will not let both be true anyway.
  if (node.blocked) return 'blocked'
  return occupiedIds.has(node.id) ? 'occupied' : 'free'
}

export interface SlotCell {
  node: ApiStorageNode
  state: SlotState
}

/**
 * Lay a node's declared grid out as rows of slots for the map.
 *
 * Driven by the DECLARED dimensions rather than by however many cell rows
 * came back: a grid missing a cell is a real problem, and padding the row
 * with a blank makes the gap visible instead of quietly reflowing the map into
 * a shape that looks correct.
 *
 * Cells are placed by their structural (gridRow, gridColumn) — never by
 * parsing labels — so the map renders any label scheme, and any child kind
 * (a freezer's grid of racks lays out the same way as a box's positions).
 */
export function buildSlotMap(
  parent: ApiStorageNode,
  cells: ApiStorageNode[],
  occupiedIds: ReadonlySet<string>,
): Array<Array<SlotCell | null>> {
  const rows = parent.gridRows ?? 0
  const columns = parent.gridColumns ?? 0
  if (rows < 1 || columns < 1) return []

  const byCoord = new Map(
    cells
      .filter((c) => c.gridRow != null && c.gridColumn != null)
      .map((c) => [`${c.gridRow}:${c.gridColumn}`, c]),
  )
  const grid: Array<Array<SlotCell | null>> = []
  for (let r = 1; r <= rows; r += 1) {
    const row: Array<SlotCell | null> = []
    for (let c = 1; c <= columns; c += 1) {
      const cell = byCoord.get(`${r}:${c}`)
      row.push(cell ? { node: cell, state: slotState(cell, occupiedIds) } : null)
    }
    grid.push(row)
  }
  return grid
}

/** Spreadsheet-style row label: 0→A, 25→Z, 26→AA. Mirrors the server. */
export function rowLabel(index: number): string {
  let label = ''
  let n = index
  while (n >= 0) {
    label = String.fromCharCode(65 + (n % 26)) + label
    n = Math.floor(n / 26) - 1
  }
  return label
}
